/* ──────────────────────────────────────────────────────────────
   screens/bundleManager.tsx · Reusable bundle component manager
   Pick a parent item → view/add/edit/remove components → price.
   Rendered standalone on the Bundles screen AND embedded on the
   Items screen. Returns a <View> (host provides the ScrollView).
   ────────────────────────────────────────────────────────────── */

import React, { useState, useEffect, useCallback } from 'react';
import { View, Text, ActivityIndicator, TouchableOpacity, Platform, ViewStyle } from 'react-native';

import { useTheme } from '../context/themeContext';
import {
  Icon, Button, Chip, AppDropdown, Modal, ConfirmDialog, Empty, useToast,
} from '../components/ui';
import CommonInput from '../components/commonInput';
import { getItems, getItem, createItem, updateItem, deleteItem, publishItem } from '../services/catalog/item';
import {
  getComponents, addComponent, updateComponent, removeComponent, relinkPrice,
} from '../services/catalog/bundle';
import { getItemTypes, createItemType } from '../services/catalog/itemType';
import { handleApiError } from '../utils/errorHandler';
import { showSuccess } from '../utils/successHandler';
import type { CatalogItem } from '../schemas/catalog/item';
import type { BundleComponent, RelationType } from '../schemas/catalog/bundle';

const RELATIONS: RelationType[] = ['BUNDLE_DYNAMIC', 'BUNDLE_STATIC', 'ACCESSORY', 'SUBSTITUTE'];

// skuOrReference is globally unique (even across deleted/archived rows), so a
// reused name can be impossible to claim. When that happens we store the name
// with a hidden, unique token — ` ⟦<token>⟧` — and strip it for display so the
// user only ever sees the clean name they typed.
const displayName = (s?: string | null): string => (s || '').replace(/\s*⟦[^⟧]*⟧\s*$/u, '').trim();
const uniqueSku = (name: string): string =>
  `${name} ⟦${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}⟧`;

// Coerce a raw field value (number | numeric string | JSON string | object)
// into a finite number, or null.
const toNumber = (raw0: any): number | null => {
  let raw: any = raw0;
  if (typeof raw === 'string') {
    try { raw = JSON.parse(raw.trim()); } catch { /* plain numeric string — Number() handles it */ }
  }
  if (raw && typeof raw === 'object') raw = raw.amount ?? raw.value ?? raw.price ?? raw.minorUnits;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const fieldKeyOf = (f: any): string => f.fieldDefinition?.fieldKey ?? f.fieldKey ?? '';
const valueOf = (f: any): any =>
  f?.valueJson ?? f?.value ?? f?.valueNumber ?? f?.valueDecimal ?? f?.valueString ?? f?.valueText;

// Pull a price out of a *fully fetched* item. Prefers the global `base_price`,
// then a location-scoped `base_price`, then any other `*price*` field (e.g.
// `monthly_price`) so a component still shows a price when base_price is unset.
const extractPrice = (it: any): number | null => {
  const fvs: any[] = it?.fieldValues || [];
  const candidates: any[] = [
    fvs.find(f => fieldKeyOf(f) === 'base_price' && !f.locationId),
    fvs.find(f => fieldKeyOf(f) === 'base_price'),
    fvs.find(f => /price/i.test(fieldKeyOf(f)) && !f.locationId),
    fvs.find(f => /price/i.test(fieldKeyOf(f))),
  ];
  for (const fv of candidates) {
    if (!fv) continue;
    const n = toNumber(valueOf(fv));
    if (n != null) return n;
  }
  return null;
};

export default function BundleManager() {
  const { theme, isDark } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows, font } = theme;
  const toast = useToast();

  const [items, setItems] = useState<CatalogItem[]>([]);
  const [parentId, setParentId] = useState('');
  const [components, setComponents] = useState<BundleComponent[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingComps, setLoadingComps] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [childId, setChildId] = useState('');
  const [relation, setRelation] = useState<RelationType>('BUNDLE_DYNAMIC');
  const [qty, setQty] = useState('1');
  const [delComp, setDelComp] = useState<BundleComponent | null>(null);
  const [removing, setRemoving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editComp, setEditComp] = useState<BundleComponent | null>(null);
  const [editRelation, setEditRelation] = useState<RelationType>('BUNDLE_DYNAMIC');
  const [editQty, setEditQty] = useState('1');
  const [savingEdit, setSavingEdit] = useState(false);
  const [allBundles, setAllBundles] = useState<{ id: string; label: string; count: number }[]>([]);
  const [loadingBundles, setLoadingBundles] = useState(true);
  // Named-bundle creation: a bundle is a catalog item of the "Bundle" type whose
  // name (skuOrReference) is whatever the user types.
  const [bundleTypeId, setBundleTypeId] = useState<string>('');
  const [addBundleOpen, setAddBundleOpen] = useState(false);
  const [newBundleName, setNewBundleName] = useState('');
  const [creatingBundle, setCreatingBundle] = useState(false);
  // Rename / delete a bundle.
  type BundleRow = { id: string; label: string; count: number };
  const [editBundle, setEditBundle] = useState<BundleRow | null>(null);
  const [editBundleName, setEditBundleName] = useState('');
  const [savingBundleName, setSavingBundleName] = useState(false);
  const [delBundle, setDelBundle] = useState<BundleRow | null>(null);
  const [deletingBundle, setDeletingBundle] = useState(false);
  // Reliable price source: item id → base_price, fetched via getItem (the list
  // response doesn't carry base_price, so we resolve it here).
  const [priceMap, setPriceMap] = useState<Record<string, number | null>>({});

  const label = useCallback(
    (id?: string) => { const it = items.find(x => x.id === id); return it ? (displayName(it.skuOrReference) || it.id.slice(0, 8)) : (id || '—'); },
    [items],
  );

  const priceOf = useCallback((id?: string): number | null => (id && id in priceMap ? priceMap[id] : null), [priceMap]);

  const fmtINR = (n: number | null) => (n == null ? '—' : '₹' + Number(n).toLocaleString('en-IN'));

  // Resolve base_price for a set of items via getItem, merge into priceMap.
  const loadPrices = useCallback(async (ids: (string | undefined)[]) => {
    const uniq = Array.from(new Set(ids.filter(Boolean) as string[]));
    if (!uniq.length) return;
    const entries = await Promise.all(uniq.map(async (id) => {
      try { return [id, extractPrice(await getItem(id))] as const; }
      catch { return [id, null] as const; }
    }));
    setPriceMap(prev => { const next = { ...prev }; for (const [id, p] of entries) next[id] = p; return next; });
  }, []);

  const partsTotal = components.reduce((sum, c) => {
    const p = priceOf(c.childItemId);
    return p == null ? sum : sum + p * (Number(c.quantity) || 1);
  }, 0);

  // Re-fetch one item → refresh its label data + its price in the map.
  const refreshItem = useCallback(async (id: string) => {
    if (!id) return;
    try {
      const fresh: any = await getItem(id);
      setItems(prev => prev.map(p => (p.id === id ? { ...p, ...fresh } : p)));
      setPriceMap(prev => ({ ...prev, [id]: extractPrice(fresh) }));
    } catch { /* non-fatal */ }
  }, []);

  const autoRelinkIfDynamic = useCallback(async (id: string) => {
    if (!id) return;
    try {
      const r = await getComponents(id);
      const comps = (r as any).items ?? [];
      if (comps.some((c: any) => c.relationType === 'BUNDLE_DYNAMIC')) {
        await relinkPrice(id, { mode: 'recompute' });
        await refreshItem(id);
      }
    } catch { /* non-fatal */ }
  }, [refreshItem]);

  // A bundle is an item of the "Bundle" type (named by the user). Components can
  // only ever be attached to a Bundle-type item, so we probe components ONLY for
  // those — never for the whole catalog. Fanning `getComponents` out across every
  // item (an O(items) request storm) saturates the browser's ~6-connection pool
  // and, because RN keeps screens mounted, starves every other screen's requests.
  const refreshBundles = useCallback(async (its: CatalogItem[], btId?: string) => {
    setLoadingBundles(true);
    try {
      // Without a resolved Bundle type we can't identify bundles — skip the
      // fan-out entirely rather than probe all items.
      const bundleItems = btId ? its.filter(it => (it as any).itemTypeId === btId) : [];
      const results = await Promise.all(bundleItems.map(async (it) => {
        let count = 0;
        try { count = (((await getComponents(it.id)) as any).items ?? []).length; }
        catch { /* count stays 0 — the bundle still lists */ }
        return { id: it.id, label: displayName(it.skuOrReference) || it.id.slice(0, 8), count };
      }));
      setAllBundles(results);
      loadPrices(results.map(b => b.id));
    } finally { setLoadingBundles(false); }
  }, [loadPrices]);

  // Find the "Bundle" item type, creating it the first time if needed.
  const resolveBundleType = useCallback(async (): Promise<string> => {
    if (bundleTypeId) return bundleTypeId;
    const types = await getItemTypes();
    const existing = (types.items ?? []).find((t: any) => (t.name || '').toLowerCase() === 'bundle');
    const id = existing?.id ?? (await createItemType({ name: 'Bundle' } as any)).id;
    setBundleTypeId(id);
    return id;
  }, [bundleTypeId]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Resolve the Bundle type up front so named bundles list correctly.
        let btId = '';
        try { btId = await resolveBundleType(); } catch { /* non-fatal */ }
        const res = await getItems({ limit: 200 });
        const live = (res.items ?? []).filter((it: any) => it.currentState !== 'ARCHIVED');
        setItems(live);
        refreshBundles(live, btId);
      } catch (err) { handleApiError(err, 'Could not load items'); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadComponents = useCallback(async (id: string) => {
    if (!id) { setComponents([]); return; }
    setLoadingComps(true);
    try {
      const res = await getComponents(id);
      const comps = (res as any).items ?? [];
      setComponents(comps);
      loadPrices([id, ...comps.map((c: any) => c.childItemId)]);
    } catch (err) { handleApiError(err, 'Could not load components'); }
    finally { setLoadingComps(false); }
  }, [loadPrices]);

  useEffect(() => { loadComponents(parentId); }, [parentId, loadComponents]);

  const onAdd = async () => {
    if (!childId) return;
    setBusy(true);
    try {
      await addComponent(parentId, { childItemId: childId, relationType: relation, quantity: Number(qty) || 1 });
      showSuccess({}, 'Component added', 'Bundle updated.');
      setAddOpen(false); setChildId(''); setQty('1');
      await loadComponents(parentId);
      await autoRelinkIfDynamic(parentId);
      refreshBundles(items, bundleTypeId);
    } catch (err) { handleApiError(err, 'Could not add component'); }
    finally { setBusy(false); }
  };

  const confirmRemove = async () => {
    if (!delComp) return;
    setRemoving(true);
    try {
      await removeComponent(parentId, delComp.childItemId);
      showSuccess({}, 'Component removed', 'Bundle updated.');
      setDelComp(null);
      await loadComponents(parentId);
      await autoRelinkIfDynamic(parentId);
      refreshBundles(items, bundleTypeId);
    } catch (err) { handleApiError(err, 'Could not remove component'); }
    finally { setRemoving(false); }
  };

  const onQty = async (c: BundleComponent, delta: number) => {
    const current = Number(c.quantity) || 1;
    const next = Math.max(1, current + delta);
    if (next === current) return;
    try {
      await updateComponent(parentId, c.childItemId, { quantity: next });
      await loadComponents(parentId);
      await autoRelinkIfDynamic(parentId);
      refreshBundles(items, bundleTypeId);
    } catch (err) { handleApiError(err, 'Could not update component'); }
  };

  const openEdit = (c: BundleComponent) => {
    setEditComp(c);
    setEditRelation(c.relationType);
    setEditQty(String(Number(c.quantity) || 1));
  };

  const saveEdit = async () => {
    if (!editComp) return;
    setSavingEdit(true);
    try {
      await updateComponent(parentId, editComp.childItemId, { relationType: editRelation, quantity: Number(editQty) || 1 });
      showSuccess({}, 'Component updated', 'Bundle updated.');
      setEditComp(null);
      await loadComponents(parentId);
      await autoRelinkIfDynamic(parentId);
      refreshBundles(items, bundleTypeId);
    } catch (err) { handleApiError(err, 'Could not update component'); }
    finally { setSavingEdit(false); }
  };

  // Create a named bundle (a Bundle-type catalog item), then select it.
  // Delete is a soft-delete, and skuOrReference is unique across ALL states, so
  // old archived bundles can keep a name reserved. On a 409 we free the name by
  // tombstoning any archived bundles of that name, then retry — so reusing a
  // deleted bundle's name "just works".
  const createBundle = async () => {
    const name = newBundleName.trim();
    if (!name) { toast.error('Enter a bundle name.'); return; }
    setCreatingBundle(true);
    try {
      const btId = await resolveBundleType();
      let created: any;
      try {
        created = await createItem({ itemTypeId: btId, skuOrReference: name } as any);
      } catch (e: any) {
        if (e?.response?.status !== 409) throw e;
        // Cache-bust so we get a fresh list that includes the archived rows.
        const byName = (list: any) => (list?.items ?? []).filter(
          (i: any) => i.itemTypeId === btId && (i.skuOrReference || '').toLowerCase() === name.toLowerCase(),
        );
        let sameName = byName(await getItems({ limit: 200, _ts: Date.now() } as any));
        // The default list usually hides archived items; try an archived-scoped
        // query so we can find and free a deleted bundle's reserved name.
        if (sameName.length === 0) {
          for (const params of [{ state: 'ARCHIVED' }, { status: 'ARCHIVED' }, { includeArchived: true }]) {
            try {
              const found = byName(await getItems({ limit: 200, ...params, _ts: Date.now() } as any));
              if (found.length) { sameName = found; break; }
            } catch { /* param not supported — try the next */ }
          }
        }
        const liveDup = sameName.find((i: any) => i.currentState !== 'ARCHIVED');
        if (liveDup) {
          toast.error(`A bundle named "${name}" already exists. Pick a different name.`);
          return;
        }
        const archivedDups = sameName.filter((i: any) => i.currentState === 'ARCHIVED');
        if (archivedDups.length > 0) {
          // We can see the archived rows — free the name cleanly, then retry.
          await Promise.all(
            archivedDups.map((d: any) =>
              updateItem(d.id, { skuOrReference: `${name} ·deleted·${d.id.slice(0, 8)}` }).catch(() => {})),
          );
          created = await createItem({ itemTypeId: btId, skuOrReference: name } as any);
        } else {
          // The archived row reserving this name is hidden from the API, so we
          // can't clear it. Store a hidden unique token; the user still sees the
          // clean name everywhere.
          created = await createItem({ itemTypeId: btId, skuOrReference: uniqueSku(name) } as any);
        }
      }
      // Publish the bundle so it goes live on the public catalog / storefront.
      try { await publishItem(created.id); } catch { /* non-fatal — stays draft */ }
      const res = await getItems({ limit: 200, _ts: Date.now() } as any);
      const live = (res.items ?? []).filter((it: any) => it.currentState !== 'ARCHIVED');
      setItems(live);
      await refreshBundles(live, btId);
      setParentId(created.id);
      setAddBundleOpen(false);
      setNewBundleName('');
      showSuccess({}, 'Bundle ready', `"${name}" is ready — add components below.`);
    } catch (err) { handleApiError(err, 'Could not create bundle'); }
    finally { setCreatingBundle(false); }
  };

  // Rename a bundle (its skuOrReference).
  const saveBundleName = async () => {
    if (!editBundle) return;
    const name = editBundleName.trim();
    if (!name) { toast.error('Enter a bundle name.'); return; }
    setSavingBundleName(true);
    try {
      await updateItem(editBundle.id, { skuOrReference: name });
      setItems(prev => prev.map(i => (i.id === editBundle.id ? { ...i, skuOrReference: name } : i)));
      await refreshBundles(items.map(i => (i.id === editBundle.id ? { ...i, skuOrReference: name } : i)), bundleTypeId);
      setEditBundle(null);
      showSuccess({}, 'Bundle renamed', `Now called "${name}".`);
    } catch (err) { handleApiError(err, 'Could not rename bundle'); }
    finally { setSavingBundleName(false); }
  };

  // Delete a bundle. Delete is a soft-delete (archive) and skuOrReference is
  // unique across ALL states, so an archived bundle would keep reserving its
  // name. We first rename it to a unique tombstone, then archive — freeing the
  // original name so it can be reused immediately.
  const confirmDeleteBundle = async () => {
    if (!delBundle) return;
    setDeletingBundle(true);
    try {
      try {
        await updateItem(delBundle.id, { skuOrReference: `${delBundle.label} ·deleted·${delBundle.id.slice(0, 8)}` });
      } catch { /* non-fatal: if rename fails the name just stays reserved */ }
      await deleteItem(delBundle.id);
      if (parentId === delBundle.id) setParentId('');
      const next = items.filter(i => i.id !== delBundle.id);
      setItems(next);
      await refreshBundles(next, bundleTypeId);
      showSuccess({}, 'Bundle deleted', `"${delBundle.label}" was removed. The name is free to reuse.`);
      setDelBundle(null);
    } catch (err) { handleApiError(err, 'Could not delete bundle'); }
    finally { setDeletingBundle(false); }
  };

  const card: ViewStyle = { backgroundColor: colors.card, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r3, ...(shadows.sm as ViewStyle) };
  // The dropdown lists the user's named bundles (not every catalog item).
  const bundleOptions = allBundles.map(b => ({ label: b.label, value: b.id }));
  // Only PUBLISHED items can be added as components (no drafts/deactivated/archived).
  // Exclude bundles themselves so a bundle can't be nested into the picker by accident.
  const childOptions = items
    .filter(i => i.id !== parentId && (i as any).currentState === 'PUBLISHED' && (i as any).itemTypeId !== bundleTypeId)
    .map(i => ({ label: i.skuOrReference || i.id.slice(0, 8), value: i.id }));
  const labelSt = { fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink2, fontFamily: font.sans };

  return (
    <View>
      {loading ? (
        <ActivityIndicator color={colors.accent} />
      ) : (
        <>
          <View style={[card, { padding: 16, marginBottom: 16, gap: 10 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <View style={{ flex: 1, minWidth: 220, gap: 8 }}>
                <Text style={labelSt}>Bundle</Text>
                <AppDropdown placeholder="Select a bundle…" value={parentId} items={bundleOptions} onSelect={setParentId} isDark={isDark} compact fieldStyle={{ maxWidth: 380 }} />
              </View>
              <Button
                kind="primary"
                size="sm"
                icon="plus"
                hoverLift
                onPress={() => { setNewBundleName(''); setAddBundleOpen(true); }}
              >
                Add Bundle
              </Button>
            </View>
          </View>

          {/* Always-visible list of every bundle (item with components). */}
          <View style={[card, { marginBottom: 16 }]}>
            <View style={{ padding: 13, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: colors.line, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans }}>Your bundles</Text>
              <Chip tone="accent">{allBundles.length}</Chip>
            </View>
            <View style={{ padding: 16 }}>
              {loadingBundles ? (
                <ActivityIndicator color={colors.accent} />
              ) : allBundles.length === 0 ? (
                <Text style={{ fontSize: fontSize.sm, color: colors.ink3, fontFamily: font.sans }}>
                  No bundles yet. Click “Add Bundle” to create one, then add its components.
                </Text>
              ) : (
                <View style={{ gap: 8 }}>
                  {allBundles.map(b => {
                    const selected = b.id === parentId;
                    return (
                      <TouchableOpacity
                        key={b.id}
                        activeOpacity={0.7}
                        onPress={() => setParentId(b.id)}
                        style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8, borderWidth: 1, borderColor: selected ? colors.accent : colors.line, backgroundColor: selected ? colors.accentSoft : colors.card, borderRadius: radii.r2, paddingVertical: 10, paddingHorizontal: 12, ...(Platform.OS === 'web' ? { cursor: 'pointer' } as any : {}) }}
                      >
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                          <Icon name="layers" size={15} color={selected ? colors.accentInk : colors.ink3} />
                          <Text numberOfLines={1} style={{ fontFamily: font.mono, fontSize: fontSize.sm, color: selected ? colors.accentInk : colors.ink }}>{b.label}</Text>
                        </View>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <Text style={{ fontFamily: font.mono, fontSize: fontSize.sm, color: selected ? colors.accentInk : colors.ink2 }}>{fmtINR(priceOf(b.id))}</Text>
                          <Chip tone="outline">{b.count} component{b.count === 1 ? '' : 's'}</Chip>
                          {/* Rename / delete — isolated so they don't select the row. */}
                          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }} onStartShouldSetResponder={() => true}>
                            <Button kind="ghost" size="xs" iconOnly icon="edit" aria-label="Rename bundle" onPress={() => { setEditBundle(b); setEditBundleName(b.label); }} />
                            <Button kind="danger" size="xs" iconOnly icon="trash" aria-label="Delete bundle" onPress={() => setDelBundle(b)} />
                          </View>
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              )}
            </View>
          </View>

          {parentId ? (
            <View style={card}>
              <View style={{ padding: 13, paddingHorizontal: 16, borderBottomWidth: 1, borderBottomColor: colors.line, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink, fontFamily: font.sans }}>Components</Text>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <Button kind="accent" size="sm" icon="plus" onPress={() => setAddOpen(true)}>Add component</Button>
                </View>
              </View>
              <View style={{ padding: 16 }}>
                {loadingComps ? (
                  <ActivityIndicator color={colors.accent} />
                ) : components.length === 0 ? (
                  <Empty title="No components">Add components to build this bundle.</Empty>
                ) : (
                  <View style={{ gap: 8 }}>
                    {components.map(c => (
                      <View key={c.id} style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, padding: 10 }}>
                        <View style={{ flex: 1, minWidth: 140, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                          <Icon name="box" size={15} color={colors.ink3} />
                          <Text numberOfLines={1} style={{ flexShrink: 1, fontFamily: font.mono, fontSize: fontSize.sm, color: colors.ink }}>{label(c.childItemId)}</Text>
                          <Chip tone="outline">{c.relationType}</Chip>
                        </View>
                        {(() => {
                          const unit = priceOf(c.childItemId);
                          const q = Number(c.quantity) || 1;
                          return (
                            <Text style={{ fontFamily: font.mono, fontSize: fontSize.sm, color: unit == null ? colors.ink3 : colors.ink, minWidth: 72, textAlign: 'right' }}>
                              {unit == null ? 'On enquiry' : fmtINR(unit * q)}
                              {unit != null && q > 1 ? <Text style={{ color: colors.ink3 }}>{`  (${fmtINR(unit)}×${q})`}</Text> : null}
                            </Text>
                          );
                        })()}
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                          <Button kind="ghost" size="xs" iconOnly icon="minus" aria-label="Decrease" onPress={() => onQty(c, -1)} />
                          <Text style={{ fontFamily: font.mono, fontSize: fontSize.sm, color: colors.ink, minWidth: 24, textAlign: 'center' }}>×{c.quantity ?? 1}</Text>
                          <Button kind="ghost" size="xs" iconOnly icon="plus" aria-label="Increase" onPress={() => onQty(c, 1)} />
                          <Button kind="ghost" size="xs" iconOnly icon="edit" aria-label="Edit" onPress={() => openEdit(c)} />
                          <Button kind="danger" size="xs" iconOnly icon="trash" aria-label="Remove" onPress={() => setDelComp(c)} />
                        </View>
                      </View>
                    ))}
                    <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, borderTopWidth: 1, borderTopColor: colors.line, paddingTop: 12, marginTop: 4 }}>
                      <Text style={{ fontSize: fontSize.sm, color: colors.ink2, fontFamily: font.sans }}>
                        Total from parts <Text style={{ color: colors.ink3 }}>(Σ price × qty)</Text>
                      </Text>
                      <Text style={{ fontFamily: font.mono, fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: colors.ink }}>{fmtINR(partsTotal)}</Text>
                    </View>
                    <View style={{ gap: 2 }}>
                      <Text style={{ fontSize: fontSize.sm, color: colors.ink2, fontFamily: font.sans }}>
                        Bundle's current price <Text style={{ fontFamily: font.mono, color: colors.ink }}>{fmtINR(priceOf(parentId))}</Text>
                      </Text>
                      <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: font.sans }}>
                        Dynamic bundles roll up from their parts automatically.
                      </Text>
                    </View>
                  </View>
                )}
              </View>
            </View>
          ) : (
            <Empty title="Pick a bundle">Select a bundle above (or create one with “Add Bundle”) to manage its components.</Empty>
          )}
        </>
      )}

      {/* Create a named bundle */}
      <Modal
        open={addBundleOpen}
        onClose={() => setAddBundleOpen(false)}
        title="New bundle"
        size="sm"
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setAddBundleOpen(false)}>Cancel</Button>
            <Button kind="accent" icon="check" disabled={!newBundleName.trim() || creatingBundle} onPress={createBundle}>
              {creatingBundle ? 'Creating…' : 'Create bundle'}
            </Button>
          </View>
        }
      >
        <View style={{ gap: 8 }}>
          <Text style={labelSt}>Bundle name</Text>
          <CommonInput
            value={newBundleName}
            onChangeText={setNewBundleName}
            placeholder="e.g. Starter Pack"
            isDark={isDark}
            inputProps={{ autoFocus: true, onSubmitEditing: createBundle }}
          />
          <Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontFamily: font.sans }}>
            Give the bundle a name, then add the items it contains as components.
          </Text>
        </View>
      </Modal>

      {/* Rename a bundle */}
      <Modal
        open={!!editBundle}
        onClose={() => setEditBundle(null)}
        title="Rename bundle"
        size="sm"
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setEditBundle(null)}>Cancel</Button>
            <Button kind="accent" icon="check" disabled={!editBundleName.trim() || savingBundleName} onPress={saveBundleName}>
              {savingBundleName ? 'Saving…' : 'Save'}
            </Button>
          </View>
        }
      >
        <View style={{ gap: 8 }}>
          <Text style={labelSt}>Bundle name</Text>
          <CommonInput
            value={editBundleName}
            onChangeText={setEditBundleName}
            placeholder="Bundle name"
            isDark={isDark}
            inputProps={{ autoFocus: true, onSubmitEditing: saveBundleName }}
          />
        </View>
      </Modal>

      {/* Delete a bundle */}
      <ConfirmDialog
        visible={!!delBundle}
        title="Delete bundle?"
        message={delBundle ? `"${delBundle.label}" will be removed. Its component items are not deleted — only the bundle.` : ''}
        confirmText={deletingBundle ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        onConfirm={confirmDeleteBundle}
        onCancel={() => setDelBundle(null)}
        onClose={() => setDelBundle(null)}
      />

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add component"
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setAddOpen(false)}>Cancel</Button>
            <Button kind="accent" icon="plus" disabled={!childId || busy} onPress={onAdd}>{busy ? 'Adding…' : 'Add'}</Button>
          </View>
        }
      >
        <View style={{ gap: 12 }}>
          {childOptions.length === 0 ? (
            <View style={{ borderWidth: 1, borderColor: colors.line, borderRadius: radii.r2, padding: 12, backgroundColor: colors.paper2 }}>
              <Text style={{ fontSize: fontSize.sm, color: colors.ink2, fontFamily: font.sans }}>
                No other items to add. A bundle needs at least one item besides the parent — create another item first, then add it here.
              </Text>
            </View>
          ) : (
            <>
              <View style={{ gap: 6 }}>
                <Text style={labelSt}>Component item</Text>
                <AppDropdown placeholder="Select component…" value={childId} items={childOptions} onSelect={setChildId} isDark={isDark} />
              </View>
              <View style={{ gap: 6 }}>
                <Text style={labelSt}>Relation</Text>
                <AppDropdown placeholder="Relation" value={relation} items={RELATIONS.map(r => ({ label: r, value: r }))} onSelect={(v) => setRelation(v as RelationType)} isDark={isDark} />
              </View>
              <View style={{ gap: 6 }}>
                <Text style={labelSt}>Quantity</Text>
                <CommonInput value={qty} onChangeText={(t) => setQty(t.replace(/[^0-9]/g, '').slice(0, 4))} placeholder="e.g. 1" isDark={isDark} inputProps={{ keyboardType: 'numeric' }} />
              </View>
            </>
          )}
        </View>
      </Modal>

      <Modal
        open={!!editComp}
        onClose={() => setEditComp(null)}
        title="Edit component"
        footer={
          <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'flex-end' }}>
            <Button onPress={() => setEditComp(null)}>Cancel</Button>
            <Button kind="accent" icon="check" disabled={savingEdit} onPress={saveEdit}>{savingEdit ? 'Saving…' : 'Save'}</Button>
          </View>
        }
      >
        <View style={{ gap: 12 }}>
          <View style={{ gap: 6 }}>
            <Text style={labelSt}>Component</Text>
            <Text style={{ fontFamily: font.mono, fontSize: fontSize.sm, color: colors.ink }}>{editComp ? label(editComp.childItemId) : ''}</Text>
          </View>
          <View style={{ gap: 6 }}>
            <Text style={labelSt}>Relation</Text>
            <AppDropdown placeholder="Relation" value={editRelation} items={RELATIONS.map(r => ({ label: r, value: r }))} onSelect={(v) => setEditRelation(v as RelationType)} isDark={isDark} />
          </View>
          <View style={{ gap: 6 }}>
            <Text style={labelSt}>Quantity</Text>
            <CommonInput value={editQty} onChangeText={(t) => setEditQty(t.replace(/[^0-9]/g, '').slice(0, 4))} placeholder="e.g. 1" isDark={isDark} inputProps={{ keyboardType: 'numeric' }} />
          </View>
        </View>
      </Modal>

      <ConfirmDialog
        visible={!!delComp}
        title="Remove component?"
        message={delComp ? `"${label(delComp.childItemId)}" will be removed from this bundle.` : ''}
        confirmText={removing ? 'Removing…' : 'Remove'}
        cancelText="Cancel"
        onConfirm={confirmRemove}
        onCancel={() => setDelComp(null)}
        onClose={() => setDelComp(null)}
      />
    </View>
  );
}
